import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown, Glasses, Box, MonitorPlay, Sparkles, Lightbulb, Camera, Wand2, Tv, Puzzle, Shield, Cpu, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWordPressNavigation, useWordPressMegaNavSettings } from "@/hooks/useWordPress";
import logo from "@/assets/famous-group-logo.png";
import vixiLogo from "@/assets/vixi-logo.svg";
import eeLogo from "@/assets/ee-logo.png";
import type { MegaNavSettings } from "@/types/wordpress";

// Lazy-load mega menus (they pull in framer-motion)
const ExperiencesMegaMenu = lazy(() => import("@/components/mega-nav/ExperiencesMegaMenu"));
const VixiMegaMenu = lazy(() => import("@/components/mega-nav/VixiMegaMenu"));
const WorkMegaMenu = lazy(() => import("@/components/mega-nav/WorkMegaMenu"));

const Header = () => {
  const { data: navSettings, isLoading: navLoading } = useWordPressNavigation();
  const { data: megaNav } = useWordPressMegaNavSettings();

  const megaMenuLabels = new Set([
    ...(megaNav?.experiences?.items?.filter(i => i.title?.trim()).length ? ["Our Experiences"] : []),
    ...(megaNav?.work?.items?.filter(i => i.title?.trim()).length ? ["Our Work"] : []),
  ]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const ctaLabel = navSettings?.ctaLabel || "Get in Touch";
  const ctaLink = navSettings?.ctaLink || "/contact";

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseEnter = useCallback((label: string) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setActiveDropdown(label);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  }, []);

  const closeDropdown = useCallback(() => setActiveDropdown(null), []);

  const closeMobile = useCallback(() => {
    setIsMobileMenuOpen(false);
    setMobileExpanded(null);
  }, []);

  const fallbackNavItems = [
    { label: "Vixi", path: "/vixi" },
    { label: "EE", path: "https://www.eliteedge.tv/", external: true },
    { label: "Our Experiences", path: "/solutions" },
    { label: "Our Work", path: "/work" },
    { label: "Company", path: "/company" },
  ];
  const navItems = fallbackNavItems;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 animate-[fadeIn_0.4s_ease-out_both] ${
          activeDropdown
            ? "bg-black border-b border-white/10"
            : isScrolled
              ? "bg-black/80 backdrop-blur-md border-b border-white/10"
              : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center">
              <img src={logo} alt="The Famous Group" className="h-8 md:h-10 w-auto" />
            </a>

            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => {
                const isMega = megaMenuLabels.has(item.label);
                if (isMega) {
                  return (
                    <div
                      key={item.label}
                      className="relative"
                      onMouseEnter={() => handleMouseEnter(item.label)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <Link
                        to={item.path}
                        onClick={() => window.scrollTo({ top: 0 })}
                        className="flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors tracking-wide uppercase"
                      >
                        {item.label === "Vixi" ? (
                          <img src={vixiLogo} alt="Vixi" className="h-10 w-auto" />
                        ) : (
                          item.label
                        )}
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeDropdown === item.label ? "rotate-180" : ""}`} />
                      </Link>
                    </div>
                  );
                }
              return (item as any).external ? (
                  <a
                    key={item.label}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors tracking-wide uppercase"
                  >
                    {item.label === "EE" ? (
                      <span className="relative group inline-flex items-center">
                        <img src={eeLogo} alt="EE" className="h-5 w-auto" />
                        <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap flex flex-col items-center leading-tight bg-[#000000]/85 px-3 py-2 rounded-sm">
                          <span className="text-sm font-medium tracking-wide">Elite Edge Productions</span>
                          <span className="text-[70%] text-foreground/70 tracking-wide">A Famous Group company</span>
                        </span>
                      </span>
                    ) : (
                      item.label
                    )}
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => window.scrollTo({ top: 0 })}
                    className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors tracking-wide uppercase"
                  >
                    {item.label === "Vixi" ? (
                    <span className="relative group inline-flex items-center">
                      <img src={vixiLogo} alt="Vixi" className="h-10 w-auto" />
                      <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap flex flex-col items-center leading-tight bg-[#000000]/85 px-3 py-2 rounded-sm">
                        <span className="text-sm font-medium tracking-wide">Vixi Suite™</span>
                        <span className="text-[70%] text-foreground/70 tracking-wide">The #1 Fan Engagement Platform</span>
                      </span>
                    </span>
                    ) : (
                      item.label
                    )}
                  </Link>
                );
              })}
              <Link to={ctaLink} onClick={() => window.scrollTo({ top: 0 })}>
                <Button className={`bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2 text-sm font-semibold tracking-wide uppercase transition-opacity duration-300 ${navLoading ? "opacity-0" : "opacity-100"}`}>
                  {ctaLabel}
                </Button>
              </Link>
            </nav>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-foreground p-2"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mega dropdowns — lazy loaded */}
        {activeDropdown && (
          <Suspense fallback={null}>
            <div onMouseEnter={() => handleMouseEnter(activeDropdown)} onMouseLeave={handleMouseLeave}>
              {activeDropdown === "Our Experiences" && <ExperiencesMegaMenu onClose={closeDropdown} />}
              {activeDropdown === "Vixi" && <VixiMegaMenu onClose={closeDropdown} />}
              {activeDropdown === "Our Work" && <WorkMegaMenu onClose={closeDropdown} />}
            </div>
          </Suspense>
        )}
      </header>

      {/* Mobile Menu — CSS-driven transitions instead of framer-motion */}
      <div
        className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
      >
        <div className="absolute inset-0 bg-background/95 backdrop-blur-lg" onClick={closeMobile} />
        <nav
          className={`absolute right-0 top-0 bottom-0 w-full max-w-sm bg-background border-l border-foreground/10 flex flex-col pt-24 px-8 overflow-y-auto transition-transform duration-300 ease-out ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          {navItems.map((item) => {
            const isMega = megaMenuLabels.has(item.label);
            if (!isMega) {
              return (item as any).external ? (
                <a
                  key={item.label}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobile}
                  className="text-xl font-medium text-foreground/80 hover:text-foreground transition-colors tracking-wide uppercase py-4 border-b border-foreground/10"
                >
                  {item.label === "EE" ? "Elite Edge" : item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={() => { closeMobile(); window.scrollTo({ top: 0 }); }}
                  className="text-xl font-medium text-foreground/80 hover:text-foreground transition-colors tracking-wide uppercase py-4 border-b border-foreground/10"
                >
                  {item.label}
                </Link>
              );
            }
            return (
              <div key={item.label} className="border-b border-foreground/10">
                <button
                  onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                  className="w-full flex items-center justify-between text-xl font-medium text-foreground/80 hover:text-foreground transition-colors tracking-wide uppercase py-4"
                >
                  {item.label}
                  <ChevronDown className={`w-5 h-5 transition-transform ${mobileExpanded === item.label ? "rotate-180" : ""}`} />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-200 ${mobileExpanded === item.label ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
                >
                  <MobileSubItems label={item.label} onClose={closeMobile} megaNav={megaNav} />
                </div>
              </div>
            );
          })}
          <Link to={ctaLink} onClick={closeMobile} className="mt-6">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 w-full py-4 text-lg font-semibold tracking-wide uppercase">
              {ctaLabel}
            </Button>
          </Link>
        </nav>
      </div>
    </>
  );
};

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Glasses, Box, MonitorPlay, Sparkles, Lightbulb, Camera, Wand2, Tv, Puzzle, Shield, Cpu, LayoutGrid,
};

const fallbackExpItems = [
  { icon: Glasses, title: "Mixed Reality", path: "/solutions" },
  { icon: Box, title: "Augmented Reality", path: "/solutions" },
  { icon: MonitorPlay, title: "Creative Production", path: "/solutions" },
  { icon: Sparkles, title: "Vixi Suite", path: "/vixi" },
  { icon: Lightbulb, title: "LightShow", path: "/vixi/lightshow" },
  { icon: Camera, title: "Vixi Capture", path: "/vixi/capture" },
];

const fallbackVixiItems = [
  { icon: Camera, title: "Capture", path: "/vixi/capture" },
  { icon: Wand2, title: "Magic", path: "/vixi/magic" },
  { icon: Tv, title: "Streaming", path: "/vixi/streaming" },
  { icon: Lightbulb, title: "LightShow", path: "/vixi/lightshow" },
  { icon: Puzzle, title: "Add-Ons", path: "/vixi/add-ons" },
  { icon: Shield, title: "Licensing", path: "/vixi/licensing" },
];

const fallbackWorkItems = [
  { icon: Glasses, title: "Mixed Reality", path: "/work?category=Mixed+Reality#more-work" },
  { icon: Box, title: "Augmented Reality", path: "/work?category=Augmented+Reality#more-work" },
  { icon: MonitorPlay, title: "Creative Production", path: "/work?category=Creative+Production#more-work" },
  { icon: Sparkles, title: "Vixi Suite", path: "/work?category=Vixi+Suite#more-work" },
  { icon: Cpu, title: "AI Powered", path: "/work?category=AI+Powered#more-work" },
  { icon: LayoutGrid, title: "All Projects", path: "/work?category=All#more-work" },
];

const MobileSubItems = ({ label, onClose, megaNav }: { label: string; onClose: () => void; megaNav?: MegaNavSettings | null }) => {
  if (label === "Our Experiences") {
    const cmsItems = megaNav?.experiences?.items?.filter(item => item.title?.trim());
    const items = cmsItems?.length
      ? cmsItems.map(item => ({ icon: ICON_MAP[item.icon] || Sparkles, title: item.title, path: item.link, external: item.external }))
      : fallbackExpItems;
    return <MobileItemList items={items} onClose={onClose} />;
  }
  if (label === "Vixi") {
    const cmsItems = megaNav?.vixi?.items?.filter(item => item.title?.trim());
    const items = cmsItems?.length
      ? cmsItems.map(item => ({ icon: ICON_MAP[item.icon] || Sparkles, title: item.title, path: item.link }))
      : fallbackVixiItems;
    return <MobileItemList items={items} onClose={onClose} />;
  }
  if (label === "Our Work") {
    const cmsItems = megaNav?.work?.items?.filter(item => item.title?.trim());
    const items = cmsItems?.length
      ? cmsItems.map(item => ({ icon: ICON_MAP[item.icon] || Sparkles, title: item.title, path: `/work?category=${encodeURIComponent(item.category || item.title)}#more-work`, external: false }))
      : fallbackWorkItems;
    return <MobileItemList items={items} onClose={onClose} />;
  }
  return null;
};

const MobileItemList = ({ items, onClose }: { items: Array<{ icon: any; title: string; path: string; external?: boolean }>; onClose: () => void }) => (
  <div className="pb-4 pl-4 space-y-1">
    {items.map((item) => {
      const Icon = item.icon;
      const content = (
        <div className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-foreground/5 transition-colors">
          <Icon className="w-4 h-4 text-foreground/50" />
          <span className="text-sm text-foreground/70">{item.title}</span>
        </div>
      );
      return item.external ? (
        <a key={item.title} href={item.path} target="_blank" rel="noopener noreferrer" onClick={onClose}>{content}</a>
      ) : (
        <Link key={item.title} to={item.path} onClick={() => { onClose(); window.scrollTo({ top: 0 }); }}>{content}</Link>
      );
    })}
  </div>
);

export default Header;